import { SORT_ORDER } from '@app/consts/common_consts';
import {
  DESIGN_CHANGE_DATA,
  STRUCTURE_LABEL,
  STRUCTURE_LABEL_ENUM,
  STRUCTURE_VALUE_ENUM,
} from '@app/consts/component_consts';
import { i18n } from '@app/modules/i18n-locales';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';

export const getDesignChangeValue = (value: any) => {
  if (value === 0) {
    return '分割不可';
  }
  return value ? 'true' : 'false';
};

export const getDivisionValue = (value: number) => {
  switch (value) {
    case 1:
      return DESIGN_CHANGE_DATA.create;
    case 2:
      return DESIGN_CHANGE_DATA.update;
    default:
      return DESIGN_CHANGE_DATA.noChange;
  }
};

/**
 * Shorten a GUID string for display purposes
 * @param {string} str - GUID string
 * @returns {string} - shortened GUID string
 */
export function shortGuid(str: string) {
  if (!str || str.length < 10) return str;
  return str.slice(0, 8) + '...' + str.slice(-8);
}

/**
 * Classify data by layer
 * @param {Array} dataArr - array of data objects like dataRes
 * @param {Array<string>} layerList - list of layers to classify
 * @returns {Object} object to classify by layer
 */
export function classifyByLayer(dataArr: any, layerList: string[]) {
  // Initialize output object with empty layers
  const result: any = {};
  layerList.forEach((layer: string) => {
    result[layer] = [];
  });

  // Iterate through each element and classify into each layer
  dataArr.forEach((item: any) => {
    const layer = item.mainInfo?.layer;
    if (layerList.includes(layer)) {
      result[layer].push(item);
    }
    // If you want to group into 'Other', you can do more processing here
    // else {
    // if (!result['Other']) result['Other'] = [];
    // result['Other'].push(item);
    // }
  });

  return result;
}
/**
 * Function to get label based on value
 * @param {string} value - 'ARC', 'MEP', or 'STR'
 * @returns {string} - corresponding label or return value if not found
 */
export function getStructureLabel(value: string) {
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || LANGUAGE_DEFAULT).toLowerCase();
  const isJapanese = currentLanguage.startsWith(LANGUAGE_DEFAULT.toLowerCase());

  // Normalize ARC/STR/MEP aliases to values in STRUCTURE_LABEL.
  const normalizedValue = value?.toUpperCase();
  let normalizedStructureValue = value;
  if (normalizedValue === 'ARC') {
    normalizedStructureValue = STRUCTURE_VALUE_ENUM.ARC;
  } else if (normalizedValue === 'STR') {
    normalizedStructureValue = STRUCTURE_VALUE_ENUM.STR;
  } else if (normalizedValue === 'MEP') {
    normalizedStructureValue = STRUCTURE_VALUE_ENUM.MEP;
  }

  const found = STRUCTURE_LABEL.find(
    (item) => item.value === normalizedStructureValue || item.code === value || item.label === value
  );

  let result = value;
  if (found) {
    result = isJapanese ? found.label : found.value;
  }

  return result;
}

/**
 * Function to get value enum based on label enum
 * @param {string} label - '建築', '構造', or '設備' from STRUCTURE_LABEL_ENUM
 * @returns {string} - corresponding value from STRUCTURE_VALUE_ENUM ('ARC', 'STR', 'MEP')
 */
export function getStructureTypeValue(label: string): string {
  switch (label) {
    case STRUCTURE_LABEL_ENUM.ARC:
      return STRUCTURE_VALUE_ENUM.ARC;
    case STRUCTURE_LABEL_ENUM.STR:
      return STRUCTURE_VALUE_ENUM.STR;
    case STRUCTURE_LABEL_ENUM.MEP:
      return STRUCTURE_VALUE_ENUM.MEP;
    default:
      return label; // Return original value if not found
  }
}

export function formatDateTime(str: string) {
  const date = new Date(str);
  const pad = (n: any) => n.toString().padStart(2, '0');

  const month = pad(date.getMonth() + 1); // getMonth trả về 0-11
  const day = pad(date.getDate());
  const year = date.getFullYear();

  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${month}/${day}/${year} - ${hours}:${minutes}:${seconds}`;
}

/**
 * Function to get sort order for DataTable
 * @param {any} sortOrder - sort order which can be 'ASC', 'DESC', 1, or -1
 * @returns {1 | -1 | 0} - returns 1 for ascending, -1 for descending, and 0 for no sort
 */
export const getSortOrderDataTable = (sortOrder: any): 1 | -1 | 0 => {
  if (sortOrder === SORT_ORDER.ASC || sortOrder === 1) return 1 as const;
  if (sortOrder === SORT_ORDER.DESC || sortOrder === -1) return -1 as const;
  return 0 as const;
};

/**
 * Function to get sort order for API
 * @param {any} sortOrder - sort order which can be 'ASC', 'DESC', 1, -1, 0, null, or undefined
 * @returns {string} - returns 'ASC' for ascending, 'DESC' for descending, defaults to 'ASC' for other cases
 */
export const getSortOrderApi = (sortOrder: any): string => {
  // Handle numeric values
  if (sortOrder === 1) return SORT_ORDER.ASC as const;
  if (sortOrder === -1) return SORT_ORDER.DESC as const;

  // Handle string values (case-insensitive)
  if (typeof sortOrder === 'string') {
    const upperSort = sortOrder.toUpperCase();
    if (upperSort === SORT_ORDER.ASC) return SORT_ORDER.ASC as const;
    if (upperSort === SORT_ORDER.DESC) return SORT_ORDER.DESC as const;
  }

  // Default to ASC for 0, null, undefined, or any other value
  return SORT_ORDER.ASC as const;
};
